import { Injectable } from '@angular/core';
import { Country, QualificationCategory, SpokenLanguage as SpokenLanguage } from '@app/core/models';
import { ViewOptions, ViewService } from '@makiwin/ngx-drupal8-rest';
import { Observable, map, mergeMap, of, tap } from 'rxjs';
import { I18nService } from '../../i18n/i18n.service';

export enum StaticValues {
  spokenLanguages = 'spokenLanguages',
  courseTemplates = 'courseTemplates',
  ridingStyle = 'ridingStyle',
  recreationalCourseType = 'recreationalCourseType',
  classSizes = 'classSizes',
  countries = 'countries',
  skills = 'skills',
  qualifications = 'qualifications',
  trainingLevels = 'trainingLevels',
  waterConditions = 'waterConditions',
  schoolFacilities = 'schoolFacilities',
  recreationalCourseCategories = 'recreationalCourseCategories',
}

const configuration: {
  [key: string]: {
    endpoint: string;
    transform?: (data: any[]) => any[];
  };
} = {
  [StaticValues.spokenLanguages]: { endpoint: '/api/spoken-languages' },
  [StaticValues.courseTemplates]: { endpoint: '/api/v1.1/course_templates.json' },
  [StaticValues.ridingStyle]: { endpoint: '/api/v1/terms/riding_style.json' },
  [StaticValues.recreationalCourseType]: { endpoint: '/api/v1/terms/recreational_course_type.json' },
  [StaticValues.classSizes]: { endpoint: '/api/class_size' },
  [StaticValues.countries]: { endpoint: '/api/countries' },
  [StaticValues.skills]: { endpoint: '/api/v1/terms/job_skills.json' },
  [StaticValues.waterConditions]: { endpoint: '/api/v1/terms/water_conditions.json' },
  [StaticValues.schoolFacilities]: { endpoint: '/api/v1/terms/school_facilities.json' },
  [StaticValues.recreationalCourseCategories]: { endpoint: '/api/v1/terms/recreational_category.json' },
  [StaticValues.qualifications]: {
    endpoint: '/api/v1/terms/qualifications.json',
    transform: (qualifications: any[]) => {
      return qualifications.map((qualification) => {
        const translated = qualification.description__value.replace(/<(\/|)p>|\n/g, '').split('<br />');
        if (translated.length > 1) {
          qualification.translatedName = translated[0];
          qualification.translatedAltName = translated[1];
        } else {
          qualification.translatedName = qualification.field_short_name;
          qualification.translatedAltName = qualification.field_alt_name;
        }
        qualification.locked = true;
        return qualification;
      });
    },
  },
  [StaticValues.trainingLevels]: { endpoint: '/api/training' },
};

@Injectable({
  providedIn: 'root',
})
export class StaticValuesListService {
  private cache: { [key: string]: any[] } = {};

  constructor(private viewService: ViewService, private i18nService: I18nService) {}

  /**
   * Get a static values list in the current language.
   * @param staticValues
   * Static values list to get.
   * @param viewOptions
   * Options options to send  for the rquest
   * @returns
   * An observable of the static values list.
   */
  getValuesCurrentLanguage<T>(staticValues: StaticValues, viewOptions?: ViewOptions): Observable<T[]> {
    viewOptions = viewOptions || {};
    viewOptions.filters = { ...(viewOptions.filters || {}), ...{ langcode: this.i18nService.language } };

    return this.getValues<T>(staticValues, viewOptions);
  }

  /**
   * Get a static values list.
   * @param staticValues
   * Static values list to get.
   * @param viewOptions
   * Options options to send  for the rquest
   * @returns
   * An observable of the static values list.
   */
  getValues<T>(staticValues: StaticValues, viewOptions?: ViewOptions): Observable<T[]> {
    let cache: T[] | null;

    let keyCache = staticValues.toString();
    if (viewOptions) {
      keyCache += JSON.stringify(viewOptions);
    }

    if ((cache = this.getFromCache<T>(keyCache))) {
      return of(cache);
    }
    return of(this.cache).pipe(
      mergeMap((currentCache) => {
        if (currentCache[keyCache]) {
          return of(currentCache[keyCache]);
        }
        return this.viewService.get(configuration[staticValues].endpoint, viewOptions).pipe(
          tap((data) => {
            if (configuration[staticValues].transform) {
              data = (configuration[staticValues].transform as any)(data);
            }
            this.setInCache<T>(keyCache, data);
          })
        );
      })
    );
  }

  /**
   * Get the list of qualifications in the current langauge.
   *
   * @returns
   * Observable of the list of qualifications.
   */
  getQualifications(): Observable<QualificationCategory[]> {
    return this.getValuesCurrentLanguage<QualificationCategory>(StaticValues.qualifications);
  }

  /**
   * Get the list of countries in current language.
   *
   * @returns
   * Observable of the list of countries in current language.
   */
  getCountries(): Observable<Country[]> {
    return this.getValues<Country>(StaticValues.countries, { filters: { langcode: this.i18nService.language } });
  }

  /**
   * Get the list of spoken languages.
   *
   * @returns
   * Observable of the list of spoken languages.
   */
  getSpokenLanguages(): Observable<SpokenLanguage[]> {
    return this.getValues<SpokenLanguage>(StaticValues.spokenLanguages);
  }

  getLangIdByCode(langCode: string): Observable<number | undefined> {
    return this.getSpokenLanguages().pipe(map((langs) => langs.find((lang) => lang.lang_code === langCode)?.lang_id));
  }

  /**
   * Set the caache for the static values list.
   * @param keyCache
   * Key of the cache.
   * @param values
   * Values to set in the cache.
   *
   */
  private setInCache<T>(keyCache: string, values: T[]) {
    this.cache[keyCache] = values;
  }

  /**
   * Get the static values list from the cache.
   * @param keyCache
   * Key of the cache.
   * @returns
   * The static values list from the cache.
   */
  private getFromCache<T>(keyCache: string): T[] | null {
    return this.cache[keyCache];
  }
}
